import React, { type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import { useAuth } from '@sine/auth-client';
import { type IApplicationFrameProps, ApplicationFrame as SineApplicationFrame } from '@sine/sine-application-frame';
import { useTeamSwitcher } from '@sine/sine-team-switcher';
import { FullScreenError, Loader, useNotification } from '@sine/sine-ui-components';
import UICVersion from '@sine/sine-ui-components/version';

import apiVersionService from '../external/apiVersion/apiVersion.service';
import TeamRequiredPage from '../pages/TeamRequiredPage/TeamRequiredPage';
import useActivePage from '../shared/hooks/useActivePage';
import type { PageLabel } from '../shared/pages/pages.constants';
import { usePermissions } from '../shared/providers/permissions.provider';

export type ApplicationFrameProps = PropsWithChildren<unknown>;

function getActivePage(page: PageLabel) {
  switch (page) {
    case 'Team Details':
    case 'API Keys':
    case 'Billing':
      return 'Settings';
    default:
      return page;
  }
}

export default function ApplicationFrame({ children }: ApplicationFrameProps) {
  const navigate = useNavigate();
  const { visiblePages } = usePermissions();
  const { authState, logout } = useAuth();
  const { currentTeam, isLoading: teamsAreLoading, error: teamError } = useTeamSwitcher();
  const { addNotification } = useNotification();
  const { show: showIntercom } = useIntercom();

  const defaultPage = useMemo(
    () => ({
      label: visiblePages[0]?.label as PageLabel,
      route: visiblePages[0]?.path,
    }),
    [visiblePages],
  );

  const activePage = useActivePage(defaultPage);

  const [logoutInProgress, setLogoutInProgress] = useState(false);

  const productLinks = useMemo<IApplicationFrameProps['productLinks']>(() => {
    return {
      admin: undefined,
      core: window.env.REACT_APP_SINE_CORE_CLIENT,
      workflows: window.env.REACT_APP_SINE_WORKFLOWS_CLIENT,
      companies: window.env.REACT_APP_SINE_COMPANIES_CLIENT,
    };
  }, []);

  const onLogOutClick = useCallback<IApplicationFrameProps['onLogOutClick']>(async () => {
    try {
      setLogoutInProgress(true);
      await logout();
    } catch (err) {
      addNotification({
        variant: 'danger',
        heading: 'Log out failed',
        message: 'Check your connection and try again. If the problem persists contact Sine support.',
      });
    } finally {
      setLogoutInProgress(false);
    }
  }, [addNotification, logout]);

  const onProfileClick = useCallback<IApplicationFrameProps['onProfileClick']>(() => {
    window.location.href = `${window.env.REACT_APP_SINE_CORE_CLIENT}/user-settings`;
  }, []);

  const versions = useMemo<IApplicationFrameProps['versionInfo']>(
    () => [
      {
        name: 'admin-client',
        value: window.env.REACT_APP_CLIENT_VERSION,
      },
      {
        name: 'core-api',
        value: apiVersionService.getApiVersion('core'),
      },
      {
        name: 'plugins-api',
        value: apiVersionService.getApiVersion('plugins'),
      },
      {
        name: 'sine-ui-components',
        value: UICVersion,
      },
    ],
    [],
  );

  if (logoutInProgress || teamsAreLoading) {
    return <Loader show fillAndCenter />;
  }

  const bootstrapError = teamError ? (
    <div>
      <FullScreenError
        type='unknown'
        heading='Unexpected Error'
        actionText='Refresh the page'
        onAction={() => window.location.reload()}
      >
        Unexpected error occurred bootstrapping Sine Admin. Check your connection and refresh the page. If the problem
        persists contact Sine support.
      </FullScreenError>
    </div>
  ) : undefined;

  const missingRequirement = !currentTeam ? <TeamRequiredPage /> : undefined;

  switch (authState) {
    case 'loggedIn': {
      return (
        <SineApplicationFrame
          productBrand='admin'
          productLinks={productLinks}
          versionInfo={versions}
          onLogOutClick={onLogOutClick}
          onProfileClick={onProfileClick}
          actionLinks={visiblePages}
          activePage={getActivePage(activePage.label)}
          onHelpClick={showIntercom}
          onProductBrandClick={() => navigate('/')}
          children={bootstrapError || missingRequirement || children}
        />
      );
    }
    case 'loggedOut': {
      window.location.href = `${window.env.REACT_APP_SINE_CORE_CLIENT}/login?redirect=${encodeURIComponent(
        window.location.href,
      )}`;
      return null;
    }
    case 'loading':
    default:
      return <Loader show fillAndCenter />;
  }
}
